import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { AlertTriangle, ArrowLeft, Boxes, CheckCircle2, Download, FileJson, FileUp, Layers, Package, PackageCheck, PackageOpen, PackagePlus, Rocket, Search, ShieldCheck, Sparkles, Trash2, Upload, XCircle } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, InlineAlert, Input, KeyValue, PageHeader, Skeleton, StatCard, Stepper, Switch, Textarea, Tooltip, cn, notifySaveFailed, notifySaved, toast } from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic, usePaletteActions } from "../app/console-context";
import { TYPE_ORDER, formatBytes, useCatalogue, useGallery, usePackage, usePackageActions, usePackagePlan, usePackages, type ExportPreview, type GalleryEntry, type ImportAction, type ImportPlan, type ImportResult, type PackageStatus, type SolutionPackage } from "../features/packages/api";

const STATUS_TONE: Record<PackageStatus, "outline" | "success" | "info" | "danger"> = { Draft: "outline", Exported: "info", Imported: "success", Failed: "danger" };
const ACTION_TONE: Record<ImportAction, "success" | "info" | "outline" | "danger" | "warning"> = { create: "success", update: "info", unchanged: "outline", unsupported: "warning", invalid: "danger" };

function typeLabel(t: (k: string, o?: Record<string, unknown>) => string, type: string) {
  return t(`admin:releases.types.${type}`, { defaultValue: type });
}

function TypeBadge({ type }: { type: string }) {
  const { t } = useTranslation();
  return <Badge variant="gold" size="sm">{typeLabel(t, type)}</Badge>;
}

function StatusBadge({ status }: { status: PackageStatus }) {
  const { t } = useTranslation();
  return <Badge variant={STATUS_TONE[status]} size="sm" dot>{t(`admin:packages.status.${status}`)}</Badge>;
}

function TypeChips({ byType, max = 6 }: { byType: Record<string, number>; max?: number }) {
  const { t } = useTranslation();
  const entries = Object.entries(byType).sort((a, b) => TYPE_ORDER.indexOf(a[0]) - TYPE_ORDER.indexOf(b[0]));
  return (
    <div className="flex flex-wrap gap-1">
      {entries.slice(0, max).map(([type, count]) => <Badge key={type} variant="outline" size="sm"><span className="tabular">{count}</span> {typeLabel(t, type)}</Badge>)}
      {entries.length > max ? <Badge variant="outline" size="sm">+{entries.length - max}</Badge> : null}
    </div>
  );
}

/** Packages: gallery (Blank platform, Competition Department), export wizard with dependency resolution, upload → plan → import into a draft release. */
export function PackagesPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePermission();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const canExport = can("platform.packages.export");
  const canImport = can("platform.packages.import");

  useHelpTopic({ title: t("admin:packages.title"), docsAnchor: "packages", body: (<><p>{t("admin:packages.help.what")}</p><p>{t("admin:packages.help.import")}</p><p>{t("admin:packages.help.secrets")}</p></>) });
  usePaletteActions(useMemo(() => [
    ...(canExport ? [{ id: "packages.export", label: t("admin:packages.exportPackage"), icon: PackagePlus, permission: "platform.packages.export", run: () => setExporting(true) }] : []),
    ...(canImport ? [{ id: "packages.import", label: t("admin:packages.importPackage"), icon: FileUp, permission: "platform.packages.import", run: () => setImporting(true) }] : []),
  ], [canExport, canImport, t]));

  const packages = usePackages();
  const gallery = useGallery();

  if (id) return <PackageDetail id={id} onBack={() => navigate("/packages")} />;

  const rows = packages.data ?? [];
  const lastExport = rows.filter((p) => p.exportedAt).sort((a, b) => (b.exportedAt ?? "").localeCompare(a.exportedAt ?? ""))[0];
  const lastImport = rows.filter((p) => p.importedAt).sort((a, b) => (b.importedAt ?? "").localeCompare(a.importedAt ?? ""))[0];
  const templates = (gallery.data ?? []).length;

  return (
    <div>
      <PageHeader title={t("admin:packages.title")} description={t("admin:packages.subtitle")} actions={
        <div className="flex items-center gap-2">
          {canImport ? <Button variant="outline" onClick={() => setImporting(true)}><FileUp /> {t("admin:packages.importPackage")}</Button> : null}
          {canExport ? <Button variant="gold" onClick={() => setExporting(true)}><PackagePlus /> {t("admin:packages.exportPackage")}</Button> : null}
        </div>
      } />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label={t("admin:packages.stored")} value={rows.length} icon={Package} tone="gold" footer={t("admin:packages.storedHint")} />
        <StatCard label={t("admin:packages.templates")} value={templates} icon={Layers} tone="sea" footer={t("admin:packages.templatesHint")} />
        <StatCard label={t("admin:packages.lastExport")} value={rows.filter((p) => p.status === "Exported").length} icon={PackageCheck} tone="neutral" footer={lastExport ? t("admin:packages.byAt", { name: lastExport.exportedByName ?? "—", when: formatDateTime(lastExport.exportedAt!, locale) }) : t("admin:packages.nothingYet")} />
        <StatCard label={t("admin:packages.lastImport")} value={rows.filter((p) => p.status === "Imported").length} icon={PackageOpen} tone="camel" footer={lastImport ? t("admin:packages.byAt", { name: lastImport.importedByName ?? "—", when: formatDateTime(lastImport.importedAt!, locale) }) : t("admin:packages.nothingYet")} />
      </div>

      <Card className="mb-6">
        <CardHeader><CardTitle className="flex items-center gap-2"><Sparkles className="size-4 text-aegold-700" /> {t("admin:packages.gallery")}</CardTitle><CardDescription>{t("admin:packages.galleryHint")}</CardDescription></CardHeader>
        <CardContent>
          {gallery.isLoading ? <Skeleton className="h-40" /> : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {(gallery.data ?? []).map((g) => <GalleryCard key={`${g.kind}:${g.key}:${g.packageId ?? ""}`} entry={g} canExport={canExport} onOpen={(pid) => navigate(`/packages/${pid}`)} />)}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Boxes className="size-4 text-aegold-700" /> {t("admin:packages.stored")}</CardTitle><CardDescription>{t("admin:packages.storedHint")}</CardDescription></CardHeader>
        <CardContent>
          {packages.isLoading ? <Skeleton className="h-48" /> : rows.length === 0 ? (
            <EmptyState icon={Package} title={t("admin:packages.empty")} description={t("admin:packages.emptyHint")} action={canImport ? <Button variant="outline" onClick={() => setImporting(true)}><FileUp /> {t("admin:packages.importPackage")}</Button> : undefined} />
          ) : (
            <PackageTable rows={rows} onOpen={(p) => navigate(`/packages/${p.id}`)} />
          )}
        </CardContent>
      </Card>

      <ExportWizard open={exporting} onOpenChange={setExporting} onExported={(p) => navigate(`/packages/${p.id}`)} />
      <ImportDialog open={importing} onOpenChange={setImporting} onUploaded={(p) => navigate(`/packages/${p.id}`)} />
    </div>
  );
}

function GalleryCard({ entry, canExport, onOpen }: { entry: GalleryEntry; canExport: boolean; onOpen: (packageId: string) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { exportTemplate } = usePackageActions();
  const ar = locale.startsWith("ar");
  const Icon = entry.key === "blank" ? Layers : entry.kind === "builtin" ? Boxes : Package;
  return (
    <Card className="flex flex-col p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="grid size-10 place-items-center rounded-xl bg-aegold-50 text-aegold-700"><Icon className="size-5" /></div>
        <Badge variant="outline" size="sm">{t(`admin:packages.galleryKind.${entry.kind}`)}{entry.version ? ` · v${entry.version}` : ""}</Badge>
      </div>
      <h3 className="mt-3 font-heading text-lg font-semibold">{ar && entry.nameAr ? entry.nameAr : entry.name}</h3>
      <p className="mt-1 flex-1 text-sm leading-relaxed text-muted-foreground">{ar && entry.descriptionAr ? entry.descriptionAr : entry.description}</p>
      <div className="mt-3"><TypeChips byType={entry.itemsByType} /></div>
      <div className="mt-4 flex items-center justify-between gap-2 border-t border-sand-200 pt-3">
        <span className="text-xs text-muted-foreground tabular">{entry.itemCount} {t("admin:releases.items").toLowerCase()}</span>
        {entry.kind === "package" && entry.packageId ? (
          <Button size="sm" variant="outline" onClick={() => onOpen(entry.packageId!)}>{t("admin:common.open")}</Button>
        ) : canExport ? (
          <Button size="sm" variant="gold" loading={exportTemplate.isPending} disabled={entry.itemCount === 0} onClick={() => exportTemplate.mutate({ templateKey: entry.key }, { onSuccess: (p) => { notifySaved(p, t("admin:packages.wizard.exported")); onOpen(p.id); } })}><Download /> {t("admin:packages.exportTemplate")}</Button>
        ) : null}
      </div>
    </Card>
  );
}

function PackageTable({ rows, onOpen }: { rows: SolutionPackage[]; onOpen: (p: SolutionPackage) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-sand-600"><tr className="border-b border-sand-200 [&>th]:px-3 [&>th]:py-2 [&>th]:text-start"><th>{t("admin:packages.columns.package")}</th><th>{t("admin:common.status")}</th><th>{t("admin:packages.columns.items")}</th><th>{t("admin:packages.columns.size")}</th><th>{t("admin:packages.columns.provenance")}</th><th>{t("admin:packages.columns.changeSet")}</th><th>{t("admin:common.updated")}</th></tr></thead>
        <tbody>
          {rows.map((p) => {
            const byType = p.manifest.items.reduce<Record<string, number>>((acc, i) => { acc[i.type] = (acc[i.type] ?? 0) + 1; return acc; }, {});
            return (
              <tr key={p.id} onClick={() => onOpen(p)} className="cursor-pointer border-b border-sand-100 hover:bg-sand-50 [&>td]:px-3 [&>td]:py-2">
                <td><p className="font-medium">{p.name} <span className="text-xs text-muted-foreground tabular">v{p.version}</span></p><p className="font-mono text-[11px] text-muted-foreground">{p.key}{p.isTemplate ? <Badge variant="outline" size="sm" className="ms-2">{t("admin:packages.galleryKind.package")}</Badge> : null}</p></td>
                <td><StatusBadge status={p.status} /></td>
                <td><TypeChips byType={byType} max={3} /></td>
                <td className="whitespace-nowrap text-xs tabular text-muted-foreground">{formatBytes(p.sizeBytes)}</td>
                <td className="text-xs">{p.importedByName ? t("admin:packages.importedBy", { name: p.importedByName }) : p.exportedByName ? t("admin:packages.exportedBy", { name: p.exportedByName }) : t("admin:packages.uploaded")}<p className="text-[11px] text-muted-foreground">{p.publisher}</p></td>
                <td>{p.importChangeSetId ? <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/releases/${p.importChangeSetId}`); }}><Rocket /> {t("admin:packages.openRelease")}</Button> : <span className="text-xs text-muted-foreground">{t("admin:packages.noRelease")}</span>}</td>
                <td className="whitespace-nowrap text-xs text-muted-foreground">{formatDateTime(p.updatedAt, locale)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- export wizard

function slugify(s: string) {
  return s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64);
}

function ExportWizard({ open, onOpenChange, onExported }: { open: boolean; onOpenChange: (o: boolean) => void; onExported: (p: SolutionPackage) => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [meta, setMeta] = useState({ key: "", name: "", nameAr: "", version: "1.0.0", publisher: "", description: "", resolveDependencies: true, isTemplate: false });
  const [keyTouched, setKeyTouched] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [preview, setPreview] = useState<ExportPreview | null>(null);
  const catalogue = useCatalogue(open);
  const actions = usePackageActions();
  useEffect(() => { if (!open) { setStep(0); setSelected(new Set()); setPreview(null); setFilter(""); setKeyTouched(false); setMeta({ key: "", name: "", nameAr: "", version: "1.0.0", publisher: "", description: "", resolveDependencies: true, isTemplate: false }); } }, [open]);

  const groups = useMemo(() => {
    const cat = catalogue.data ?? {};
    return TYPE_ORDER.filter((type) => cat[type]).map((type) => ({ type, items: (cat[type] ?? []).filter((i) => !filter || `${i.key} ${i.name ?? ""}`.toLowerCase().includes(filter.toLowerCase())) }));
  }, [catalogue.data, filter]);

  const toggle = (id: string, on: boolean) => setSelected((prev) => { const next = new Set(prev); on ? next.add(id) : next.delete(id); return next; });
  const toggleType = (type: string, on: boolean) => setSelected((prev) => { const next = new Set(prev); for (const i of catalogue.data?.[type] ?? []) on ? next.add(`${type}:${i.key}`) : next.delete(`${type}:${i.key}`); return next; });
  const items = () => [...selected].map((id) => { const idx = id.indexOf(":"); return { type: id.slice(0, idx), key: id.slice(idx + 1) }; });

  const goReview = () => {
    if (selected.size === 0) { toast.error(t("admin:packages.wizard.nothingSelected")); return; }
    actions.preview.mutate({ items: items(), resolveDependencies: meta.resolveDependencies }, { onSuccess: (p) => { setPreview(p); setStep(2); } });
  };
  const doExport = () => actions.exportPackage.mutate({ ...meta, nameAr: meta.nameAr || null, publisher: meta.publisher || null, description: meta.description || null, items: items() }, {
    onSuccess: (p) => { notifySaved(p, t("admin:packages.wizard.exported")); onOpenChange(false); onExported(p); },
  });
  const detailsValid = /^[a-z0-9][a-z0-9-_.]{1,63}$/.test(meta.key) && meta.name.trim().length > 0 && /^\d+\.\d+\.\d+/.test(meta.version);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader><DialogTitle>{t("admin:packages.wizard.title")}</DialogTitle><DialogDescription>{t("admin:packages.wizard.hint")}</DialogDescription></DialogHeader>
        <Stepper current={step} steps={[{ key: "details", label: t("admin:packages.wizard.steps.details") }, { key: "items", label: t("admin:packages.wizard.steps.items") }, { key: "review", label: t("admin:packages.wizard.steps.review") }]} onStepClick={(i) => { if (i < step) setStep(i); }} className="mb-4" />

        {step === 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("admin:common.name")} required>{(fid) => <Input id={fid} autoFocus value={meta.name} onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value, key: keyTouched ? m.key : slugify(e.target.value) }))} dir="auto" />}</Field>
            <Field label={t("admin:common.nameAr")}>{(fid) => <Input id={fid} value={meta.nameAr} onChange={(e) => setMeta({ ...meta, nameAr: e.target.value })} dir="rtl" />}</Field>
            <Field label={t("admin:packages.wizard.key")} hint={t("admin:packages.wizard.keyHint")} required>{(fid) => <Input id={fid} value={meta.key} onChange={(e) => { setKeyTouched(true); setMeta({ ...meta, key: e.target.value }); }} dir="ltr" className="font-mono" />}</Field>
            <Field label={t("admin:common.version")} required>{(fid) => <Input id={fid} value={meta.version} onChange={(e) => setMeta({ ...meta, version: e.target.value })} dir="ltr" className="font-mono" />}</Field>
            <Field label={t("admin:packages.wizard.publisher")}>{(fid) => <Input id={fid} value={meta.publisher} onChange={(e) => setMeta({ ...meta, publisher: e.target.value })} placeholder="MOET Competition Department" dir="auto" />}</Field>
            <Field label={t("admin:common.description")} className="sm:col-span-2">{(fid) => <Textarea id={fid} rows={2} value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} dir="auto" />}</Field>
            <label className="flex items-start gap-3 rounded-lg border border-sand-200 p-3 text-sm"><Switch checked={meta.resolveDependencies} onCheckedChange={(v) => setMeta({ ...meta, resolveDependencies: v })} /><span><span className="font-medium">{t("admin:packages.wizard.resolveDeps")}</span><span className="block text-xs text-muted-foreground">{t("admin:packages.wizard.resolveDepsHint")}</span></span></label>
            <label className="flex items-start gap-3 rounded-lg border border-sand-200 p-3 text-sm"><Switch checked={meta.isTemplate} onCheckedChange={(v) => setMeta({ ...meta, isTemplate: v })} /><span className="font-medium">{t("admin:packages.wizard.isTemplate")}</span></label>
          </div>
        ) : null}

        {step === 1 ? (
          <div>
            <div className="mb-3 flex items-center gap-2">
              <div className="relative flex-1"><Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input className="ps-9" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t("admin:packages.wizard.searchItems")} dir="auto" /></div>
              <Badge variant="gold">{t("admin:packages.wizard.selected", { count: selected.size })}</Badge>
            </div>
            {catalogue.isLoading ? <Skeleton className="h-64" /> : (
              <div className="max-h-[50vh] space-y-3 overflow-y-auto pe-1">
                {groups.map(({ type, items: list }) => {
                  const all = catalogue.data?.[type] ?? [];
                  const allOn = all.length > 0 && all.every((i) => selected.has(`${type}:${i.key}`));
                  return (
                    <div key={type} className="rounded-lg border border-sand-200">
                      <div className="flex items-center justify-between gap-2 border-b border-sand-100 bg-sand-50/60 px-3 py-2">
                        <div className="flex items-center gap-2"><TypeBadge type={type} /><span className="text-xs text-muted-foreground tabular">{all.length}</span></div>
                        <label className="flex items-center gap-2 text-xs"><Checkbox checked={allOn} disabled={all.length === 0} onCheckedChange={(v) => toggleType(type, v === true)} /> {t("admin:packages.wizard.selectAllType", { type: typeLabel(t, type) })}</label>
                      </div>
                      {list.length === 0 ? <p className="px-3 py-2 text-xs text-muted-foreground">{all.length === 0 ? t("admin:packages.wizard.noItemsOfType") : "…"}</p> : (
                        <ul className="grid gap-x-3 sm:grid-cols-2">
                          {list.map((i) => { const iid = `${type}:${i.key}`; return (
                            <li key={iid}><label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-sand-50"><Checkbox checked={selected.has(iid)} onCheckedChange={(v) => toggle(iid, v === true)} /><span className="min-w-0 flex-1 truncate">{i.name || i.key}</span><span className="font-mono text-[11px] text-muted-foreground">{i.key}{i.version ? ` v${i.version}` : ""}</span></label></li>
                          ); })}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}

        {step === 2 && preview ? (
          <div>
            <p className="mb-3 text-sm text-muted-foreground">{t("admin:packages.wizard.reviewHint")}</p>
            {preview.warnings.length > 0 ? <InlineAlert tone="warning" title={t("admin:packages.wizard.warnings")} className="mb-3"><ul className="list-disc ps-4 text-xs">{preview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul></InlineAlert> : null}
            <div className="max-h-[45vh] overflow-y-auto rounded-lg border border-sand-200">
              <table className="w-full text-sm">
                <tbody>
                  {preview.items.map((i) => (
                    <tr key={`${i.type}:${i.key}`} className="border-b border-sand-100 last:border-0 [&>td]:px-3 [&>td]:py-1.5">
                      <td className="w-32"><TypeBadge type={i.type} /></td>
                      <td><span className="font-medium">{i.name}</span> <span className="font-mono text-[11px] text-muted-foreground">{i.key}{i.version ? ` v${i.version}` : ""}</span></td>
                      <td className="text-end text-xs text-muted-foreground">{i.selected ? <CheckCircle2 className="inline size-3.5 text-success-600" /> : i.requiredBy ? t("admin:packages.wizard.requiredBy", { by: i.requiredBy }) : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-muted-foreground tabular">{preview.items.length} {t("admin:releases.items").toLowerCase()} · {meta.key} v{meta.version}</p>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("admin:common.cancel")}</Button>
          {step > 0 ? <Button variant="outline" onClick={() => setStep(step - 1)}><ArrowLeft className="rtl:rotate-180" /> {t("admin:common.back")}</Button> : null}
          {step === 0 ? <Button variant="gold" disabled={!detailsValid} onClick={() => setStep(1)}>{t("admin:common.next")}</Button> : null}
          {step === 1 ? <Button variant="gold" disabled={selected.size === 0} loading={actions.preview.isPending} onClick={goReview}>{t("admin:common.preview")}</Button> : null}
          {step === 2 ? <Button variant="gold" loading={actions.exportPackage.isPending} onClick={doExport}><Download /> {t("admin:packages.wizard.exportNow")}</Button> : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- import dialog

function ImportDialog({ open, onOpenChange, onUploaded }: { open: boolean; onOpenChange: (o: boolean) => void; onUploaded: (p: SolutionPackage) => void }) {
  const { t } = useTranslation();
  const actions = usePackageActions();
  const [json, setJson] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (!open) { setJson(""); setFileName(null); } }, [open]);
  const done = (r: { package: SolutionPackage }) => { notifySaved(r, t("admin:packages.upload.uploaded")); onOpenChange(false); onUploaded(r.package); };
  const pick = (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    actions.upload.mutate(file, { onSuccess: done });
  };
  const submitJson = () => {
    try { JSON.parse(json); } catch { toast.error(t("admin:packages.upload.invalidJson")); return; }
    actions.uploadJson.mutate(json, { onSuccess: done });
  };
  const busy = actions.upload.isPending || actions.uploadJson.isPending;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader><DialogTitle>{t("admin:packages.upload.title")}</DialogTitle><DialogDescription>{t("admin:packages.upload.hint")}</DialogDescription></DialogHeader>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}
          className={cn("flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 text-sm transition-colors", dragging ? "border-aegold-500 bg-aegold-50/60" : "border-sand-300 hover:border-aegold-400 hover:bg-sand-50")}
        >
          <Upload className="size-6 text-aegold-700" />
          <span>{fileName ?? t("admin:packages.upload.drop")}</span>
          <span className="font-mono text-[11px] text-muted-foreground">.ncp-package.json</span>
          <input ref={inputRef} type="file" accept=".json,application/json" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        </button>
        <Field label={t("admin:packages.upload.paste")} hint={t("admin:packages.upload.pasteHint")}>{(fid) => <Textarea id={fid} rows={6} value={json} onChange={(e) => setJson(e.target.value)} dir="ltr" className="font-mono text-xs" placeholder='{"manifest":{…},"items":[…]}' />}</Field>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("admin:common.cancel")}</Button>
          <Button variant="gold" disabled={!json.trim()} loading={busy} onClick={submitJson}><FileJson /> {t("admin:packages.upload.uploadAction")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- detail

function PackageDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { can } = usePermission();
  const pkg = usePackage(id);
  const plan = usePackagePlan(id);
  const actions = usePackageActions();
  const p = pkg.data;
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [title, setTitle] = useState("");
  const [submit, setSubmit] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  useBreadcrumbs(useMemo(() => p ? [{ label: `${p.name} v${p.version}` }] : null, [p]));
  useEffect(() => { if (plan.data && selected === null) setSelected(new Set(plan.data.items.filter((i) => i.action === "create" || i.action === "update").map((i) => `${i.type}:${i.key}`))); }, [plan.data, selected]);
  useEffect(() => { if (p && !title) setTitle(`Install ${p.name} v${p.version}`); }, [p, title]);

  if (pkg.isLoading || !p) return <Skeleton className="h-96" />;
  const canImport = can("platform.packages.import") && p.status !== "Imported";
  const canExport = can("platform.packages.export");
  const planItems = plan.data?.items ?? [];
  const planById = new Map(planItems.map((i) => [`${i.type}:${i.key}`, i]));
  const importable = planItems.filter((i) => i.action === "create" || i.action === "update");
  const selectedCount = importable.filter((i) => selected?.has(`${i.type}:${i.key}`)).length;
  const groups = TYPE_ORDER.filter((type) => p.manifest.items.some((i) => i.type === type)).map((type) => ({ type, items: p.manifest.items.filter((i) => i.type === type) }));
  const extraTypes = [...new Set(p.manifest.items.map((i) => i.type).filter((ty) => !TYPE_ORDER.includes(ty)))].map((type) => ({ type, items: p.manifest.items.filter((i) => i.type === type) }));

  const doImport = () => actions.importPackage.mutate({ id: p.id, title: title.trim() || null, keys: selected ? [...selected] : null, submit }, {
    onSuccess: (r) => { const failed = r.items.filter((i) => i.outcome === "failed").length; failed ? toast.warning(t("admin:packages.detail.partial", { count: failed })) : notifySaved(r, t("admin:packages.detail.imported")); setResult(r); },
  });

  return (
    <div>
      <Button variant="ghost" size="sm" className="mb-2" onClick={onBack}><ArrowLeft className="rtl:rotate-180" /> {t("admin:packages.detail.back")}</Button>
      <PageHeader
        title={<span className="flex flex-wrap items-center gap-3">{p.name} <Badge variant="outline">v{p.version}</Badge> <StatusBadge status={p.status} />{p.isTemplate ? <Badge variant="gold" size="sm">{t("admin:packages.galleryKind.package")}</Badge> : null}</span>}
        description={p.description ?? undefined}
        meta={<><span className="font-mono text-xs text-muted-foreground">{p.key}</span><span className="text-xs text-muted-foreground">· {p.publisher}</span>{p.manifest.platformVersion ? <span className="text-xs text-muted-foreground">· {t("admin:packages.detail.format", { format: p.manifest.format, platform: p.manifest.platformVersion })}</span> : null}</>}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => actions.download(p).catch((e: unknown) => notifySaveFailed(e, t("admin:common.error")))}><Download /> {t("admin:packages.detail.download")}</Button>
            {p.importChangeSetId ? <Button variant="outline" onClick={() => navigate(`/releases/${p.importChangeSetId}`)}><Rocket /> {t("admin:packages.openRelease")}</Button> : null}
            {canExport && p.status !== "Imported" ? <Button variant="ghost" onClick={() => setConfirmDelete(true)}><Trash2 /> {t("admin:packages.detail.delete")}</Button> : null}
          </div>
        }
      />

      {p.error ? <InlineAlert tone="danger" title={t("admin:packages.status.Failed")} className="mb-4">{p.error}</InlineAlert> : null}

      <div className="grid gap-4 xl:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><CardTitle className="flex items-center gap-2"><Package className="size-4 text-aegold-700" /> {t("admin:packages.detail.manifest")} <Badge variant="outline" size="sm">{p.itemCount}</Badge></CardTitle><CardDescription>{t("admin:packages.detail.manifestHint")}</CardDescription></div>
              {canImport && importable.length > 0 ? <label className="flex items-center gap-2 text-xs"><Checkbox checked={selectedCount === importable.length} onCheckedChange={(v) => setSelected(v === true ? new Set(importable.map((i) => `${i.type}:${i.key}`)) : new Set())} /> {t("admin:packages.detail.selectImportable")}</label> : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {[...groups, ...extraTypes].map(({ type, items }) => (
              <div key={type} className="rounded-lg border border-sand-200">
                <div className="flex items-center gap-2 border-b border-sand-100 bg-sand-50/60 px-3 py-2"><TypeBadge type={type} /><span className="text-xs text-muted-foreground tabular">{items.length}</span></div>
                <table className="w-full text-sm">
                  <tbody>
                    {items.map((i) => {
                      const iid = `${type}:${i.key}`;
                      const pi = planById.get(iid);
                      const selectable = canImport && pi && (pi.action === "create" || pi.action === "update");
                      return (
                        <tr key={iid} className={cn("border-b border-sand-100 last:border-0 [&>td]:px-3 [&>td]:py-1.5", pi?.action === "invalid" && "bg-danger-50/40")}>
                          <td className="w-8">{selectable ? <Checkbox checked={selected?.has(iid) ?? false} onCheckedChange={(v) => setSelected((prev) => { const next = new Set(prev ?? []); v === true ? next.add(iid) : next.delete(iid); return next; })} /> : null}</td>
                          <td>
                            <p className="font-medium">{i.name || i.key} <span className="font-mono text-[11px] text-muted-foreground">{i.key}{i.version ? ` v${i.version}` : ""}</span></p>
                            {pi?.diffSummary && pi.action !== "unchanged" ? <p className="mt-0.5 whitespace-pre-line font-mono text-[11px] text-muted-foreground">{pi.diffSummary.split("\n").slice(0, 3).join("\n")}{pi.diffSummary.split("\n").length > 3 ? " …" : ""}</p> : null}
                            {pi?.issues.length ? <ul className="mt-1 space-y-0.5">{pi.issues.map((iss, n) => <li key={n} className="flex items-start gap-1 text-[11px] text-warning-800"><AlertTriangle className="mt-0.5 size-3 shrink-0" /> {iss}</li>)}</ul> : null}
                            {i.dependsOn.length > 0 ? <p className="mt-0.5 text-[11px] text-muted-foreground">→ {i.dependsOn.slice(0, 6).join(", ")}{i.dependsOn.length > 6 ? ` +${i.dependsOn.length - 6}` : ""}</p> : null}
                          </td>
                          <td className="w-44 text-end">
                            {plan.isLoading ? <Skeleton className="ms-auto h-5 w-20" /> : pi ? (
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="flex items-center gap-1">{!pi.checksumOk ? <Tooltip content={t("admin:packages.detail.checksumFailed")}><XCircle className="size-3.5 text-danger-600" /></Tooltip> : null}<Badge variant={ACTION_TONE[pi.action]} size="sm">{t(`admin:packages.detail.action.${pi.action}`)}</Badge></span>
                                {pi.currentVersion ? <span className="text-[11px] text-muted-foreground tabular">{t("admin:packages.detail.current", { version: pi.currentVersion })}</span> : null}
                              </div>
                            ) : null}
                          </td>
                          <td className="w-28 text-end"><Tooltip content={i.sha256}><span className="font-mono text-[10px] text-muted-foreground">{i.sha256.slice(0, 10)}</span></Tooltip></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2"><ShieldCheck className="size-4 text-aegold-700" /> {t("admin:packages.detail.plan")}</CardTitle><CardDescription>{t("admin:packages.detail.planHint")}</CardDescription></CardHeader>
            <CardContent className="space-y-3 text-sm">
              {plan.isLoading ? <Skeleton className="h-24" /> : plan.isError ? <InlineAlert tone="danger">{(plan.error as Error).message}</InlineAlert> : plan.data ? <PlanSummary plan={plan.data} /> : null}
            </CardContent>
          </Card>

          {canImport ? (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2"><PackageOpen className="size-4 text-aegold-700" /> {t("admin:packages.detail.import")}</CardTitle><CardDescription>{t("admin:packages.detail.importHint")}</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <Field label={t("admin:packages.detail.releaseTitle")}>{(fid) => <Input id={fid} value={title} onChange={(e) => setTitle(e.target.value)} dir="auto" />}</Field>
                <label className="flex items-center gap-3 text-sm"><Switch checked={submit} onCheckedChange={setSubmit} /> {t("admin:packages.detail.submit")}</label>
                {plan.data && importable.length === 0 ? <InlineAlert tone="info">{t("admin:packages.detail.nothingToImport")}</InlineAlert> : null}
                <Button variant="gold" className="w-full" disabled={!plan.data?.canImport || selectedCount === 0} loading={actions.importPackage.isPending} onClick={doImport}><PackageOpen /> {t("admin:packages.detail.importNow", { count: selectedCount })}</Button>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">{t("admin:packages.detail.provenance")}</CardTitle></CardHeader>
            <CardContent>
              <KeyValue columns={1} items={[
                { label: t("admin:packages.detail.sha"), value: <span className="break-all font-mono text-xs">{p.sha256}</span> },
                { label: t("admin:packages.detail.size"), value: <span className="tabular">{formatBytes(p.sizeBytes)}</span> },
                { label: t("admin:packages.detail.created"), value: `${p.manifest.createdBy || "—"} · ${p.manifest.createdAt ? formatDateTime(p.manifest.createdAt, locale) : "—"}` },
                ...(p.exportedAt ? [{ label: t("admin:packages.status.Exported"), value: t("admin:packages.byAt", { name: p.exportedByName ?? "—", when: formatDateTime(p.exportedAt, locale) }) }] : []),
                ...(p.importedAt ? [{ label: t("admin:packages.status.Imported"), value: t("admin:packages.byAt", { name: p.importedByName ?? "—", when: formatDateTime(p.importedAt, locale) }) }] : []),
                { label: t("admin:common.status"), value: p.storageKey ? t("admin:packages.detail.inStorage") : t("admin:packages.detail.inDatabase") },
                ...(p.manifest.dependencies.length > 0 ? [{ label: t("admin:packages.detail.dependencies"), value: <span className="font-mono text-[11px]">{p.manifest.dependencies.join(", ")}</span> }] : []),
              ]} />
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={!!result} onOpenChange={(o) => { if (!o) setResult(null); }}>
        <DialogContent size="md">
          <DialogHeader><DialogTitle>{t("admin:packages.detail.result")}</DialogTitle></DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-sand-200">
            <table className="w-full text-sm">
              <tbody>
                {(result?.items ?? []).filter((i) => i.outcome !== "unchanged" && i.outcome !== "skipped").map((i) => (
                  <tr key={`${i.type}:${i.key}`} className="border-b border-sand-100 last:border-0 [&>td]:px-3 [&>td]:py-1.5">
                    <td className="w-32"><TypeBadge type={i.type} /></td>
                    <td className="font-mono text-xs">{i.key}</td>
                    <td className="text-end"><Badge variant={i.outcome === "staged" ? "success" : i.outcome === "failed" ? "danger" : "outline"} size="sm">{t(`admin:packages.detail.outcome.${i.outcome}`, { defaultValue: i.outcome })}</Badge>{i.error ? <p className="mt-0.5 text-[11px] text-danger-700">{i.error}</p> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResult(null)}>{t("admin:common.close")}</Button>
            {result?.changeSetId ? <Button variant="gold" onClick={() => navigate(`/releases/${result.changeSetId}`)}><Rocket /> {t("admin:packages.detail.openChangeSet")}</Button> : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent size="sm">
          <DialogHeader><DialogTitle>{t("admin:packages.detail.delete")}</DialogTitle><DialogDescription>{t("admin:packages.detail.deleteConfirm")}</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>{t("admin:common.cancel")}</Button>
            <Button variant="destructive" loading={actions.remove.isPending} onClick={() => actions.remove.mutate(p.id, { onSuccess: (answer) => { notifySaved(answer, t("admin:packages.detail.deleted")); onBack(); } })}><Trash2 /> {t("admin:common.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PlanSummary({ plan }: { plan: ImportPlan }) {
  const { t } = useTranslation();
  const counts = plan.items.reduce<Partial<Record<ImportAction, number>>>((acc, i) => { acc[i.action] = (acc[i.action] ?? 0) + 1; return acc; }, {});
  return (
    <>
      <div className={cn("flex items-center gap-2 rounded-lg px-3 py-2 text-sm", plan.checksumOk ? "bg-success-50 text-success-800" : "bg-danger-50 text-danger-800")}>
        {plan.checksumOk ? <CheckCircle2 className="size-4" /> : <XCircle className="size-4" />}
        {plan.checksumOk ? t("admin:packages.detail.checksumOk") : t("admin:packages.detail.checksumFailed")}
      </div>
      <div className="flex flex-wrap gap-1">
        {(Object.keys(ACTION_TONE) as ImportAction[]).filter((a) => counts[a]).map((a) => <Badge key={a} variant={ACTION_TONE[a]} size="sm"><span className="tabular">{counts[a]}</span> {t(`admin:packages.detail.action.${a}`)}</Badge>)}
      </div>
      {plan.errors.length > 0 ? <InlineAlert tone="danger"><ul className="list-disc ps-4 text-xs">{plan.errors.map((e, i) => <li key={i}>{e}</li>)}</ul></InlineAlert> : null}
      {plan.warnings.length > 0 ? <InlineAlert tone="warning"><ul className="list-disc ps-4 text-xs">{plan.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul></InlineAlert> : null}
      {plan.missingDependencies.length > 0 ? (
        <div>
          <p className="text-xs font-medium">{t("admin:packages.detail.missingDeps")}</p>
          <p className="text-[11px] text-muted-foreground">{t("admin:packages.detail.missingDepsHint")}</p>
          <div className="mt-1 flex flex-wrap gap-1">{plan.missingDependencies.map((d) => <Badge key={d} variant="outline" size="sm" className="font-mono">{d}</Badge>)}</div>
        </div>
      ) : null}
    </>
  );
}
